import { useEffect } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { LEGAL_DOCS } from "./legalContent";
import { ShieldCheck, FileText, LifeBuoy, Receipt, ArrowLeft, Mail } from "lucide-react";

const iconMap = {
  privacy: ShieldCheck,
  terms: FileText,
  safety: LifeBuoy,
  refunds: Receipt,
};

const siblingLabels = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  safety: "Safety Policy",
  refunds: "Refund & Cancellation",
};

const SUPPORT_EMAIL = "support@ridemateapp.in";

// Renders text and highlights the support email as a styled inline link
const renderWithEmail = (text) => {
  if (!text.includes(SUPPORT_EMAIL)) return text;
  const parts = text.split(SUPPORT_EMAIL);
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="legal-inline-email"
          className="inline-flex items-center gap-1.5 mx-0.5 rounded-full bg-[#5FA836]/10 border border-[#5FA836]/40 text-[#5FA836] font-semibold px-2.5 py-0.5 hover:bg-[#5FA836] hover:text-white transition-colors align-baseline"
        >
          {SUPPORT_EMAIL}
        </a>
      )}
    </span>
  ));
};

export default function LegalPage() {
  const params = useParams();
  const location = useLocation();
  // Support both /privacy, /terms, /safety AND /legal/:slug
  const slug = params.slug || location.pathname.replace(/^\/+/, "").split("/")[0];
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!doc) return <Navigate to="/" replace />;

  const Icon = iconMap[doc.slug] || FileText;
  const siblings = Object.keys(LEGAL_DOCS).filter((s) => s !== doc.slug);

  return (
    <main
      data-testid={`legal-page-${doc.slug}`}
      className="pt-12 pb-24 bg-white"
    >
      <section className="relative py-14 md:py-20 border-b border-[#e5e7eb] bg-[#F9FAFB]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 font-body text-sm text-gray-600 hover:text-[#5FA836] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="mt-6 flex items-start gap-5">
            <div className="h-12 w-12 rounded-2xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836] shrink-0">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
                Legal · RideMate
              </span>
              <h1 className="mt-2 font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-[1] text-[#24303F]">
                {doc.title}
              </h1>
              <div className="mt-3 font-body text-sm text-gray-500">
                Effective Date:{" "}
                <span className="text-[#24303F] font-semibold">
                  {doc.effectiveDate}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-14 md:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div>
            {doc.intro.split("\n\n").map((p, i) => (
              <p
                key={i}
                className="font-body text-gray-700 text-[16px] leading-[1.85] mb-5"
              >
                {p}
              </p>
            ))}
          </div>

          <div className="mt-10 space-y-10">
            {doc.sections.map((sec, idx) => (
              <div
                key={`${slug}-${idx}`}
                data-testid={`legal-section-${idx}`}
              >
                <h2 className="font-display text-xl md:text-2xl font-extrabold text-[#24303F] tracking-tight">
                  {sec.title}
                </h2>
                <div className="mt-4 space-y-4">
                  {sec.paragraphs?.map((p, pi) => (
                    <p
                      key={pi}
                      className="font-body text-gray-700 text-[15.5px] leading-[1.85]"
                    >
                      {renderWithEmail(p)}
                    </p>
                  ))}
                  {sec.bullets && (
                    <ul className="pl-1 space-y-2">
                      {sec.bullets.map((b, bi) => (
                        <li
                          key={bi}
                          className="flex items-start gap-3 font-body text-gray-700 text-[15.5px] leading-relaxed"
                        >
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#5FA836] shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {sec.subGroup && (
                    <div className="mt-4">
                      <p className="font-body text-gray-700 text-[15.5px] leading-[1.85]">
                        {sec.subGroup.heading}
                      </p>
                      <ul className="mt-2 pl-1 space-y-2">
                        {sec.subGroup.bullets.map((b, bi) => (
                          <li
                            key={bi}
                            className="flex items-start gap-3 font-body text-gray-700 text-[15.5px] leading-relaxed"
                          >
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#5FA836] shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {sec.outro && (
                    <p className="font-body text-gray-700 text-[15.5px] leading-[1.85] font-medium">
                      {sec.outro}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 rounded-3xl border border-[#5FA836]/30 bg-gradient-to-br from-[#5FA836]/8 via-white to-white p-7 md:p-9">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-2xl bg-[#5FA836] grid place-items-center text-white shrink-0 shadow-[0_10px_25px_-10px_rgba(95,168,54,0.6)]">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-[#24303F]">
                  Questions about this policy?
                </h3>
                <p className="mt-2 font-body text-gray-600 text-[15px] leading-relaxed">
                  Reach out to us and we&apos;ll be happy to help.
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="legal-support-email"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#5FA836] text-white font-display text-sm font-bold px-5 py-2.5 hover:bg-[#4d8a2b] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          {/* Sibling legal docs */}
          <div className="mt-10">
            <div className="font-display text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold">
              Also read
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {siblings.map((s) => {
                const SIcon = iconMap[s];
                return (
                  <Link
                    key={s}
                    to={`/${s}`}
                    data-testid={`legal-sibling-${s}`}
                    className="group flex items-center gap-4 rounded-2xl border border-[#e5e7eb] bg-white p-5 hover:border-[#5FA836]/60 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836]">
                      <SIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-[#24303F]">
                        {siblingLabels[s]}
                      </div>
                      <div className="font-body text-xs text-gray-500 mt-0.5">
                        Read the {siblingLabels[s].toLowerCase()} →
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
